import { sedrApi } from '../../api/sedr';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { dialogHelpTopic } from '../../help/dialogHelpTopics';
import { bindModalDismiss, createModalShell } from '../../ui/modalDialogShell';
import { downloadSedrTemplate, showSedrImportWizard } from './SedrImportWizard';
import {
  showSedrStarterSetImportDialog,
  showSedrStarterSetPreviewDialog,
} from './sedrStarterSetImportDialog';

export function showSedrImportHubDialog(options: {
  roomId: string;
  onDone: () => void;
}): void {
  const shell = createModalShell({
    titleKey: 'sedr.import.hubTitle',
    titleId: 'sedr-import-hub-title',
    helpTopic: dialogHelpTopic('sedrImportHub'),
    className: 'sedr-import-hub-dialog',
  });

  shell.body.innerHTML = `
    <div class="sedr-import-hub">
      <section class="sedr-import-hub__section">
        <div class="sedr-import-hub__copy">
          <h3 data-i18n="sedr.import.hubFile"></h3>
          <p class="muted" data-i18n="sedr.import.hubFileHint"></p>
        </div>
        <button type="button" class="button primary" id="sedr-import-hub-file" data-i18n="sedr.import.hubFile"></button>
      </section>
      <section class="sedr-import-hub__section">
        <div class="sedr-import-hub__copy">
          <h3 data-i18n="sedr.import.hubTemplate"></h3>
          <p class="muted" data-i18n="sedr.import.hubTemplateHint"></p>
        </div>
        <button type="button" class="button subtle" id="sedr-import-hub-template" data-i18n="sedr.import.hubTemplate"></button>
      </section>
      <section class="sedr-import-hub__section sedr-import-hub__section--stack">
        <div class="sedr-import-hub__copy">
          <h3 data-i18n="sedr.import.hubRequirementsSet"></h3>
          <p class="muted" data-i18n="sedr.import.hubRequirementsSetHint"></p>
        </div>
        <div class="sedr-import-hub__starter-list" id="sedr-import-hub-starter-sets"></div>
      </section>
    </div>
  `;

  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.className = 'button ghost';
  closeButton.id = 'sedr-import-hub-close';
  closeButton.setAttribute('data-i18n', 'sedr.import.hubClose');
  shell.actions.append(closeButton);

  const refreshLocale = () => applyI18n(shell.dialog);
  refreshLocale();
  const unbindLocale = bindLocaleRefresh(refreshLocale);

  const close = () => {
    unbindLocale();
    unbindDismiss();
    shell.backdrop.remove();
  };

  const unbindDismiss = bindModalDismiss({
    backdrop: shell.backdrop,
    closeButton: shell.closeButton,
    onClose: close,
  });

  closeButton.addEventListener('click', close);

  document.body.append(shell.backdrop);

  void (async () => {
    const starterSetsHost = shell.body.querySelector('#sedr-import-hub-starter-sets');
    if (!starterSetsHost) return;
    try {
      const starterSets = await sedrApi.listStarterSets('Requirements');
      if (starterSets.length === 0) {
        starterSetsHost.innerHTML = `<p class="muted" data-i18n="sedr.library.emptyRequirementsSets"></p>`;
        applyI18n(starterSetsHost);
        return;
      }

      starterSetsHost.replaceChildren();
      for (const set of starterSets) {
        const row = document.createElement('div');
        row.className = 'sedr-import-hub__starter-row';

        const meta = document.createElement('div');
        meta.className = 'sedr-import-hub__starter-meta';

        const nameEl = document.createElement('strong');
        nameEl.className = 'sedr-import-hub__starter-name';
        nameEl.textContent = set.name;

        const descriptionEl = document.createElement('p');
        descriptionEl.className = 'muted sedr-import-hub__starter-description';
        descriptionEl.textContent = set.description?.trim() ? set.description : '';

        const countEl = document.createElement('p');
        countEl.className = 'muted sedr-import-hub__starter-count';
        countEl.textContent = t('sedr.import.itemCount', { count: set.itemCount });

        meta.append(nameEl);
        if (set.description?.trim()) meta.append(descriptionEl);
        meta.append(countEl);

        const actions = document.createElement('div');
        actions.className = 'sedr-import-hub__starter-actions data-table__actions-inner';

        const previewBtn = document.createElement('button');
        previewBtn.type = 'button';
        previewBtn.className = 'button subtle';
        previewBtn.setAttribute('data-i18n', 'sedr.import.preview');
        previewBtn.addEventListener('click', () => {
          showSedrStarterSetPreviewDialog({
            setId: set.id,
            setName: set.name,
          });
        });

        const useBtn = document.createElement('button');
        useBtn.type = 'button';
        useBtn.className = 'button primary';
        useBtn.setAttribute('data-i18n', 'sedr.import.hubUse');
        useBtn.addEventListener('click', () => {
          close();
          showSedrStarterSetImportDialog({
            roomId: options.roomId,
            setId: set.id,
            setName: set.name,
            onDone: options.onDone,
          });
        });

        actions.append(previewBtn, useBtn);
        row.append(meta, actions);
        starterSetsHost.append(row);
      }
      applyI18n(starterSetsHost);
    } catch {
      starterSetsHost.innerHTML = `<p class="error-text">${t('status.error')}</p>`;
    }
  })();

  shell.body.querySelector('#sedr-import-hub-file')?.addEventListener('click', () => {
    close();
    showSedrImportWizard(options.roomId, options.onDone);
  });

  shell.body.querySelector('#sedr-import-hub-template')?.addEventListener('click', () => {
    downloadSedrTemplate();
  });
}
